import {Component, computed, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatInputModule} from '@angular/material/input';
import {DashboardPreviewService} from '../../../core/services/dashboard-preview.service';

type DashboardUserChoice = {id: string; label: string};

@Component({
  selector: 'app-switch-dashboard-dialog',
  imports: [ReactiveFormsModule, MatAutocompleteModule, MatButtonModule, MatDialogModule, MatInputModule],
  template: `
    <h2 mat-dialog-title>Switch dashboard</h2>
    <mat-dialog-content>
      <p>Choose a user to view their Dashboard in read-only mode.</p>
      <label for="dashboard-user-select">User</label>
      <input matInput id="dashboard-user-select" class="dashboard-user-select"
        placeholder="Search by name or email" autocomplete="off"
        aria-describedby="dashboard-user-hint" [formControl]="userControl"
        [matAutocomplete]="userOptions" (focus)="selectInputText($event)">
      <mat-autocomplete #userOptions="matAutocomplete" [displayWith]="displayUser" autoActiveFirstOption>
        @for (user of filteredUsers(); track user.id) {
          <mat-option [value]="user">{{ user.label }}</mat-option>
        } @empty {
          <mat-option disabled>No users found</mat-option>
        }
      </mat-autocomplete>
      @if (selectedUserId() === null) {
        <p id="dashboard-user-hint" class="selection-hint">Type a name or email, then choose a user.</p>
      } @else if (selectedUserId() === '') {
        <p id="dashboard-user-hint" class="selection-hint">Your own Dashboard uses your usual access.</p>
      } @else {
        <p id="dashboard-user-hint" class="selection-hint">You stay signed in as yourself. Editing is not available.</p>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="dialog.close()">Cancel</button>
      <button mat-flat-button class="switch-confirm" [disabled]="!canSwitch()" (click)="confirm()">Switch</button>
    </mat-dialog-actions>
  `,
  styles: `
    :host { display: block; }
    label { display: block; margin: 20px 0 8px; color: #263b3a; font-weight: 500; }
    input {
      box-sizing: border-box;
      width: 100%;
      padding: 11px 12px;
      border: 1px solid #b7d4cf;
      border-radius: 6px;
      background: #fff;
      color: #263b3a;
      font: inherit;
    }
    input:focus-visible { outline: 2px solid #008b83; outline-offset: 2px; }
    .selection-hint { margin: 12px 0 0; font-size: 13px; line-height: 20px; color: #617773; }
  `,
})
export class SwitchDashboardDialogComponent {
  readonly preview = inject(DashboardPreviewService);
  readonly dialog = inject(MatDialogRef<SwitchDashboardDialogComponent, string>);
  readonly choices = computed<DashboardUserChoice[]>(() => [
    {id: '', label: 'My Dashboard'},
    ...this.preview.users().map(user => ({
      id: String(user.id), label: user.name + (user.email ? ` — ${user.email}` : ''),
    })),
  ]);
  readonly userControl = new FormControl<DashboardUserChoice | string>(
    this.choices().find(user => user.id === String(this.preview.targetId() ?? '')) ?? '',
    {nonNullable: true},
  );
  private readonly controlValue = toSignal(this.userControl.valueChanges, {initialValue: this.userControl.value});
  readonly filteredUsers = computed(() => {
    const value = this.controlValue();
    const query = typeof value === 'string' ? value.trim().toLocaleLowerCase() : '';
    return this.choices().filter(user => user.label.toLocaleLowerCase().includes(query));
  });
  // Only an option selected from the authorized list can identify a user.
  readonly selectedUserId = computed(() => {
    const value = this.controlValue();
    return typeof value === 'string' ? null : value.id;
  });
  readonly displayUser = (user: DashboardUserChoice | string | null): string =>
    typeof user === 'string' ? user : user?.label ?? '';
  readonly canSwitch = computed(() => {
    const id = this.selectedUserId();
    return this.preview.allowed()
      && id !== null
      && id !== String(this.preview.targetId() ?? '')
      && (id === '' || this.preview.users().some(user => String(user.id) === id));
  });

  selectInputText(event: FocusEvent): void {
    if (event.target instanceof HTMLInputElement) event.target.select();
  }

  confirm(): void {
    const id = this.selectedUserId();
    if (this.canSwitch() && id !== null) this.dialog.close(id);
  }
}
